/**
 * Splits an array into multiple smaller arrays of the specified size.
 *
 * @param {any[]} input - The array to be chunked.
 * @param {number} size - The size of each chunk.
 * @return {any[][]} An array of smaller arrays, each containing elements from the original array.
 */
export const chunk = (input, size) => {
  return input.reduce((arr, item, idx) => {
    return idx % size === 0
      ? [...arr, [item]]
      : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]];
  }, []);
};

/**
 * Groups data based on a specified key and extracts specified properties.
 * If no properties are specified, the whole item is included.
 *
 * @param {Array} data - The data to be grouped.
 * @param {string} groupByKey - The key to group by (supports nested keys with dot notation).
 * @param {Array<string>} [extractProps] - The properties to extract for each group (supports nested keys with dot notation).
 * @returns {Object} The grouped data.
 */
export const groupByKey = (data, groupByKey, extractProps) => {
  if (!Array.isArray(data)) return {};

  // Helper function to get the value of a nested key
  const getNestedValue = (obj, key) =>
    key
      .split('.')
      .reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);

  return data.reduce((acc, item) => {
    const key = getNestedValue(item, groupByKey);
    if (!acc[key]) acc[key] = [];

    const extractedItem =
      extractProps && extractProps.length > 0
        ? extractProps.reduce((obj, prop) => {
            obj[prop.split('.').pop()] = getNestedValue(item, prop);
            return obj;
          }, {})
        : item;

    acc[key].push(extractedItem);
    return acc;
  }, {});
};

/**
 * Sorts an array of objects by a specified nested key in alphabetical or numeric order.
 * @param {Array<Object>} array - The array of objects to be sorted.
 * @param {string} key - The nested key of the objects by which to sort, e.g., 'class.number'.
 * @returns {Array<Object>} The sorted array.
 */
export const sortByKey = (array, key) => {
  if (!array) return [];

  const getNestedValue = (obj, key) => {
    return key.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  return array.slice().sort((a, b) => {
    const valA = getNestedValue(a, key);
    const valB = getNestedValue(b, key);

    // Convert values to numbers if they are numeric strings, otherwise compare as strings
    const numA = isNaN(valA) ? valA : Number(valA);
    const numB = isNaN(valB) ? valB : Number(valB);

    if (typeof numA === 'number' && typeof numB === 'number') {
      return numA - numB; // Numeric comparison
    } else {
      if (numA < numB) {
        return -1;
      }
      if (numA > numB) {
        return 1;
      }
      return 0;
    }
  });
};
